import { WebStorageStateStore } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';

/**
 * OIDC settings for the `workforce-dashboard` public client (Authorization
 * Code + PKCE) in the Keycloak `workforce` realm. Values mirror the contract in
 * keycloak-internal `terraform/99_workforce_dashboard`:
 *
 * - `authority`   — `https://<keycloak-host>/realms/workforce` (env, not secret)
 * - `client_id`   — `workforce-dashboard`
 * - redirect URIs — `${origin}` and `${origin}/callback` (registered per env)
 * - scope         — `openid profile email`; the `groups` claim (full path)
 *                   rides on the ID token and drives entitlement filtering.
 */

const DEFAULT_CLIENT_ID = 'workforce-dashboard';
const DEFAULT_SCOPE = 'openid profile email';

const authority = import.meta.env.VITE_OIDC_AUTHORITY;

if (!authority) {
  throw new Error('VITE_OIDC_AUTHORITY is not configured. Set it to https://<keycloak-host>/realms/workforce (see .env).');
}

const origin = window.location.origin;

export const oidcConfig: AuthProviderProps = {
  authority,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID || DEFAULT_CLIENT_ID,
  scope: import.meta.env.VITE_OIDC_SCOPE || DEFAULT_SCOPE,
  redirect_uri: `${origin}/callback`,
  post_logout_redirect_uri: origin,
  response_type: 'code',

  // Renew silently via the refresh token grant — no hidden iframe / third-party
  // cookies (Keycloak session-status iframes are unreliable behind trackers).
  automaticSilentRenew: true,
  monitorSession: false,

  // Persist tokens across reloads so a refresh does not force a new redirect.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};
